import styled from '@emotion/styled/macro'
import { Box } from '@mui/material'
import theme from 'theme'
import { RecordsGridContainer } from 'ui/components/records-grid/recordsGrid.styles'
import ColumnPicker from 'ui/pages/cpi-details/sections/devices/components/ColumnPicker'

/* The same AG Grid theme the CPI operational view uses; it grows into whatever height the card has. */
export const AssetsGridBody = styled(RecordsGridContainer)`
  position: relative;
  flex: 1;
  min-height: 0;

  /* Above the loading overlay (z-index 10), or its pulsing rows paint over a filter opened mid-refetch. */
  .ag-popup-child {
    z-index: 11;
  }
`

/* Fills the space the statistics leave, and keeps a usable height when they are open on a short screen. */
export const AssetsGridWrapper = styled(Box)`
  flex: 1 0 auto;
  min-height: 480px;
  background: ${theme.colors.surfaceContainerBackground};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  padding: ${theme.spacing.medium}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;

  .grid-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing.small}px;

    .grid-heading {
      display: flex;
      align-items: center;
      gap: ${theme.spacing.xSmall}px;
      flex-shrink: 0;
    }

    .grid-icon {
      display: inline-flex;
      width: 20px;
      height: 20px;
      align-items: center;
      justify-content: center;
      color: ${theme.colors.menuItemText};
    }

    .grid-title {
      color: ${theme.colors.menuItemText};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.header};
      font-weight: ${theme.typography.fontWeight.bold};
    }

    /* Chips share the title's row and run back from the controls, so they fill the gap between the
       two and align right. The shared container measures this width to decide how many chips fit,
       folding the rest into its own overflow menu. */
    .filter-chips-container {
      flex: 1;
      min-width: 0;
      width: auto;
      margin-right: 0;
      justify-content: flex-end;
    }

    .controls-container {
      display: flex;
      align-items: center;
      gap: ${theme.spacing.xSmall}px;
      flex-shrink: 0;
    }
  }

  .grid-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing.small}px;

    .meta-text {
      color: ${theme.colors.textCaption};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 22px;

      .meta-value {
        color: ${theme.colors.menuItemText};
      }
    }
  }

`

export const RowCapNotice = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 ${theme.spacing.medium}px;
  color: ${theme.colors.textCaption};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.normal};
  cursor: default;
`

export const ActiveFilterRow = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing.xSmall}px;

  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
    padding: ${theme.spacing.half}px ${theme.spacing.xSmall}px;
    border-radius: ${theme.attributes.borderRadius.small};
    background: ${theme.colors.chipActiveBg};
    color: ${theme.colors.legacyTextPrimary};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.bold};

    .chip-clear {
      all: unset;
      cursor: pointer;
      display: inline-flex;
      line-height: 1;
      padding: 0 2px;
      color: ${theme.colors.devicesSecondaryText};

      &:hover {
        color: ${theme.colors.legacyTextPrimary};
      }

      &:focus-visible {
        outline: 1px solid ${theme.colors.cta};
      }
    }
  }
`

/**
 * The shared column picker as the Asset Inventory design draws it: every label in text-primary,
 * a drag handle per row, and a quiet Restore default. The CPI grids keep the base styles.
 */
export const AssetColumnPicker = styled(ColumnPicker)`
  && {
    .content .list .list-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 42px;
      padding-right: ${theme.spacing.small}px;
      border-top: 2px solid transparent;

      &[draggable='true'] {
        cursor: grab;
      }

      &.is-dragging {
        opacity: 0.4;
      }

      &.is-drop-target {
        border-top-color: ${theme.colors.cta};
      }

      .MuiFormControlLabel-root {
        flex: 1;
        min-width: 0;
        padding-right: 0;

        .MuiTypography-root,
        &.is-checked .MuiTypography-root {
          color: ${theme.colors.menuItemText};
          font-weight: ${theme.typography.fontWeight.bold};
        }

        .MuiCheckbox-root svg path,
        .MuiCheckbox-root svg rect {
          stroke: ${theme.colors.menuItemText};
        }
      }

      .drag-handle {
        display: inline-flex;
        flex-shrink: 0;
        color: ${theme.colors.devicesSecondaryText};
      }
    }

    .button-wrapper button {
      border: none;
      background: transparent;
      padding-left: 0;
      color: ${theme.colors.menuItemText};
      font-weight: ${theme.typography.fontWeight.bold};

      &:disabled {
        color: ${theme.colors.devicesSecondaryText};
      }
    }
  }
`
